/*!
 * Copyright © 2018-2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import { createFeatureSelector, createSelector } from '@ngrx/store';

import { isElementNull } from '../../../../utils';
import {
  AssetMarkerInfo,
  DeviceHealthIssueCategory,
  EDeviceErrorType,
  EMarkerShapeType,
  EPlotIcon,
  EPlotType,
  EStartLocationSource,
  EStatusFilterType,
  EVehicleStatusType,
  GarminMarkerInfo,
  GeofenceMarkerInfo,
  HarshDrivingEventMarkerInfo,
  IAssetPlot,
  ICoordinates,
  IGarminStopPlot,
  IGeofencePlot,
  IHotspotPlot,
  ILatLng,
  ILatLngBounds,
  IMarkerOptions,
  IShapes,
  IVehiclePlot,
  IWorkOrderPlot,
  LatLng,
  MarkerInfo,
  MarkerShape,
  VehicleMarkerInfo,
  WorkOrderMarkerInfo
} from '../../models';
import { IDeviceHealth, IMapsState } from '../state';
import { getAllDevicesHealth } from './device-health.selector';
import { getHarshDrivingEvent } from './harsh-driving-events.selectors';
import { getVehicleStatusFilterType } from './map-options.selectors';
import {
  getAssetPlots,
  getGarminStopsPlots,
  getGeofencePlots,
  getHotspotPlots,
  getVehiclePlots,
  getWorkOrdersPlots,
  statusToFilterableStatusMap
} from './plots.selectors';

const _getPlotMarkerInfo = (plot: IGeofencePlot | IGarminStopPlot | IHotspotPlot, plotType: EPlotType): MarkerInfo => {
  const latLng = new LatLng(plot.center.Latitude, plot.center.Longitude);
  const shapes: LatLng[] = [];

  plot.shapes.Shapes.forEach((shape: IShapes) => {
    shape.Points.forEach((point: ICoordinates) => {
      const latLng2 = new LatLng(point.Latitude, point.Longitude);
      shapes.push(latLng2);
    });
  });

  let googleMarkerShape: google.maps.Polygon | google.maps.Circle = null;
  if (plot.shapes.Center) {
    const markerShape = {
      center: new LatLng(plot.shapes.Center.Latitude, plot.shapes.Center.Longitude),
      radius: plot.shapes.Radius,
      shapes,
      shapeColor: plot.shpcolor,
      shapeType: plot.shptype
    };
    googleMarkerShape = new MarkerShape(markerShape).toGoogleMapShape() as google.maps.Polygon | google.maps.Circle;
  }

  switch (plotType) {
    case EPlotType.garmin:
      return new GarminMarkerInfo(
        plot.id,
        latLng,
        plot.name,
        plotType,
        plot.extraoptionalid,
        { iconName: plot.iconclass } as IMarkerOptions,
        googleMarkerShape
      );
    default:
      return new GeofenceMarkerInfo(
        plot.id,
        latLng,
        plot.name,
        plotType,
        plot.type,
        { iconName: plot.iconclass } as IMarkerOptions,
        googleMarkerShape,
        plot.isFromSearchLight
      );
  }
};

export const getMapsState = createFeatureSelector<IMapsState>('maps');
export const getMapCenter = createSelector(
  getMapsState,
  (state: IMapsState): ILatLng => new LatLng(state.mapCenter.lat, state.mapCenter.lng)
);

export const getMapVehicleMarkers = createSelector(
  getVehiclePlots,
  getAllDevicesHealth,
  (vehiclePlots: IVehiclePlot[], devicesHealth: IDeviceHealth[]) =>
    vehiclePlots.map((plot: IVehiclePlot) => {
      const latLng = new LatLng(plot.coordinates.lat, plot.coordinates.lng);
      const deviceHealth = devicesHealth.find(x => x.vehicleId == plot.id.toString());
      const vehicleMarkerInfo = new VehicleMarkerInfo(
        plot.id,
        latLng,
        plot.vehicleName,
        plot.type,
        plot.status,
        plot.driverId,
        plot.hasNavigationDevice,
        { iconName: plot.iconClass } as IMarkerOptions,
        setDeviceHealthStatusFilterType(deviceHealth)
      );

      if (
        plot?.evBatteryData?.chargingState &&
        plot?.evBatteryData?.batteryPercentage < 100 &&
        plot.status === EVehicleStatusType.Charging
      ) {
        setEvChargingIcon(plot.evBatteryData.batteryPercentage, vehicleMarkerInfo);
      }
      return vehicleMarkerInfo;
    })
);

export const getMapVehicleMarkersFilteredByStatusType = createSelector(
  getMapVehicleMarkers,
  getVehicleStatusFilterType,
  (vehicleMarkers: VehicleMarkerInfo[], vehicleStatusTypeFilter: EStatusFilterType) => {
    const allMarkers = [...vehicleMarkers];

    switch (vehicleStatusTypeFilter) {
      case EVehicleStatusType.All:
        return allMarkers;
      case EDeviceErrorType.All:
        return allMarkers.filter((marker: VehicleMarkerInfo) => marker.deviceErrorStatuses?.length > 0);
      case EDeviceErrorType.CameraError:
        return allMarkers.filter((marker: VehicleMarkerInfo) => marker.deviceErrorStatuses?.includes(EDeviceErrorType.CameraError));
      case EDeviceErrorType.TrackerError:
        return allMarkers.filter((marker: VehicleMarkerInfo) => marker.deviceErrorStatuses?.includes(EDeviceErrorType.TrackerError));
      default:
        return allMarkers.filter(
          (marker: VehicleMarkerInfo) => statusToFilterableStatusMap.get(marker.status)?.has(vehicleStatusTypeFilter)
        );
    }
  }
);

export const getMapHotspotsMarkers = createSelector(getHotspotPlots, (hotspotPlots: IHotspotPlot[]) =>
  hotspotPlots.map((plot: IHotspotPlot) => _getPlotMarkerInfo(plot, EPlotType.hotspot))
);

export const getMapGeofenceMarkers = createSelector(getGeofencePlots, (geofencePlots: IGeofencePlot[]) =>
  geofencePlots.map((plot: IGeofencePlot) => _getPlotMarkerInfo(plot, EPlotType.geofence))
);

export const getMapGarminStopMarkers = createSelector(getGarminStopsPlots, (garminStopsPlots: IGarminStopPlot[]) =>
  garminStopsPlots.map((plot: IGarminStopPlot) => _getPlotMarkerInfo(plot, EPlotType.garmin))
);

export const getAssetsMarkers = createSelector(
  getAssetPlots,
  getVehicleStatusFilterType,
  (assetPlots: IAssetPlot[], statusFilter: EStatusFilterType) =>
    statusFilter === EVehicleStatusType.All || statusFilter === EVehicleStatusType.Stopped
      ? assetPlots.map((plot: IAssetPlot) => {
          const latLng = new LatLng(plot.center.Latitude, plot.center.Longitude);
          const mapOptions = {
            iconName: plot.iconClass
          } as IMarkerOptions;
          return new AssetMarkerInfo(plot.id, latLng, plot.name, EPlotType.asset, mapOptions);
        })
      : []
);

export const getMapWorkOrderMarkers = createSelector(getWorkOrdersPlots, (workOrdersPlots: IWorkOrderPlot[]) =>
  workOrdersPlots.map((plot: IWorkOrderPlot) => {
    const latLng = new LatLng(plot.Point.Latitude, plot.Point.Longitude);
    const className = 'workorder ' + (plot.ShapeId === 0 ? 'square' : 'circle') + plot.ColorId;
    const shapes: LatLng[] = [];
    let googleMarkerShape: google.maps.Circle = null;
    if (plot.Radius > 0) {
      const markerShape = {
        center: latLng,
        radius: plot.Radius,
        shapes,
        shapeColor: '#00FFFF',
        shapeType: EMarkerShapeType.Circle
      };
      googleMarkerShape = new MarkerShape(markerShape).toGoogleMapShape() as google.maps.Circle;

      return new WorkOrderMarkerInfo(
        plot.WorkOrderId,
        latLng,
        '',
        EPlotType.workOrder,
        plot.DriverId,
        {
          className,
          htmlContent: plot.Letter
        } as IMarkerOptions,
        googleMarkerShape,
        plot.isFromSearchLight
      );
    }
  })
);

export const getHarshDrivingEventMarker = createSelector(getHarshDrivingEvent, event =>
  !isElementNull(event)
    ? new HarshDrivingEventMarkerInfo(
        event.id,
        new LatLng(event.address.latitude, event.address.longitude),
        event.classification.toString(),
        EPlotType.harshDrivingEvent,
        {
          iconName: EPlotIcon.HarshDrivingEvent
        },
        null,
        true
      )
    : null
);

export const getIsTrafficEnabled = createSelector(getMapsState, (state: IMapsState): boolean => state.isTrafficEnabled);

export const getIsClusteringEnabled = createSelector(getMapsState, (state: IMapsState): boolean => state.isClusteringEnabled);

export const getMapBounds = createSelector(getMapsState, (state: IMapsState): ILatLngBounds => state.bounds);
export const getMapChangeBounds = createSelector(getMapsState, (state: IMapsState): ILatLng[] => state.changeBounds);
export const getIsStreetViewOpen = createSelector(getMapsState, (state: IMapsState): boolean => state.isStreetViewOpen);
export const getStreetViewLocation = createSelector(getMapsState, (state: IMapsState): ILatLng => state.streetViewLocation);
export const getShowStartingLocation = createSelector(
  getMapsState,
  (state: IMapsState): EStartLocationSource => state.showStartingLocation
);
export const getMapZoomLevel = createSelector(getMapsState, (state: IMapsState): number => state.zoomLevel);
export const getMarkerToHighlight = createSelector(getMapsState, (state: IMapsState): MarkerInfo => state.markerToHighlight);
export const getSelectedMarker = createSelector(getMapsState, (state: IMapsState): MarkerInfo => state.selectedMarker);
export const getMarkersToHighlight = createSelector(
  getAssetsMarkers,
  getMapVehicleMarkers,
  (vehicleMarkers: MarkerInfo[], assetMarkers: MarkerInfo[]): MarkerInfo[] => [...vehicleMarkers, ...assetMarkers]
);

function setEvChargingIcon(batteryPercentage: number, vehicleMarkerInfo: VehicleMarkerInfo): void {
  const percentageIconMap: [number, EPlotIcon][] = [
    [91, EPlotIcon.Charging99],
    [81, EPlotIcon.Charging90],
    [71, EPlotIcon.Charging80],
    [61, EPlotIcon.Charging70],
    [51, EPlotIcon.Charging60],
    [41, EPlotIcon.Charging50],
    [31, EPlotIcon.Charging40],
    [21, EPlotIcon.Charging30],
    [11, EPlotIcon.Charging20],
    [1, EPlotIcon.Charging10],
    [0, EPlotIcon.Charging0]
  ];
  percentageIconMap.some(([percentage, icon]) => {
    if (batteryPercentage >= percentage) {
      vehicleMarkerInfo.options.iconName = icon;
      return true;
    }
    return false;
  });
}

function setDeviceHealthStatusFilterType(deviceHealth: IDeviceHealth): EDeviceErrorType[] {
  const errorTypes: EDeviceErrorType[] = [];
  if (deviceHealth && deviceHealth.healthStatus.toUpperCase() != DeviceHealthIssueCategory.Optimal) {
    if (deviceHealth.cameraHealth.length > 0) errorTypes.push(EDeviceErrorType.CameraError);
    if (deviceHealth.componentHealth.length > 0) errorTypes.push(EDeviceErrorType.TrackerError);
  }
  return errorTypes;
}
